import axios from 'axios';
import { SavedLocation } from '../types';

// Get the Mapbox token from environment
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Interface for the route calculation results
export interface RouteResult {
  distance: number; // meters
  duration: number; // seconds
  geometry: {
    coordinates: [number, number][];
    type: string;
  };
}

// Interface for route data stored per location
export interface LocationRouteInfo {
  time: string;
  distance: string;
  durationSeconds: number;
  distanceMeters: number;
}

/**
 * Calculate routes between a sequence of locations using Mapbox Directions API
 * Each segment is calculated independently to avoid constraints from prior headings
 */
export const calculateRoutes = async (
  locations: SavedLocation[]
): Promise<{ 
  routes: {[locationId: string]: LocationRouteInfo}, 
  routeGeometry: any 
}> => {
  // We need at least 2 locations to calculate a route
  if (!locations || locations.length < 2) {
    return { routes: {}, routeGeometry: null };
  }

  // Sort locations by order to ensure proper routing sequence
  const sortedLocations = [...locations].sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : 999;
    const orderB = b.order !== undefined ? b.order : 999;
    return orderA - orderB;
  });

  try {
    const routeResults: {[locationId: string]: LocationRouteInfo} = {};
    const allCoordinates: [number, number][] = [];
    
    // Add first location coordinates
    allCoordinates.push([sortedLocations[0].longitude, sortedLocations[0].latitude]);
    
    // Calculate each segment independently
    for (let i = 1; i < sortedLocations.length; i++) {
      const startLocation = sortedLocations[i-1];
      const endLocation = sortedLocations[i];
      
      // Build coordinates string for just this segment "startLng,startLat;endLng,endLat"
      const segmentCoordinates = `${startLocation.longitude},${startLocation.latitude};${endLocation.longitude},${endLocation.latitude}`;
      
      try {
        // Make the request to the Mapbox Directions API for this segment
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${segmentCoordinates}`;
        const response = await axios.get(url, {
          params: {
            access_token: MAPBOX_TOKEN,
            geometries: 'geojson',
            overview: 'full',
            steps: 'false',
            alternatives: false,
            exclude: 'ferry',
            annotations: 'distance,duration'
          }
        });
        
        // Process the response
        const directions = response.data;
        
        if (!directions.routes || directions.routes.length === 0) {
          throw new Error(`No route found between locations ${i-1} and ${i}`);
        }
        
        // Get the route for this segment
        const route = directions.routes[0];
        
        // Add route segment coordinates to the full route
        // Skip the first point since it's already included from the previous segment
        if (route.geometry && route.geometry.coordinates) {
          const segmentCoords = route.geometry.coordinates as [number, number][];
          
          // Add all coordinates except the first one (to avoid duplication)
          if (segmentCoords.length > 1) {
            allCoordinates.push(...segmentCoords.slice(1));
          }
        }
        
        // Calculate time in a readable format
        if (route.legs && route.legs.length > 0) {
          const leg = route.legs[0];
          const durationInSeconds = leg.duration;
          const durationInMinutes = Math.round(durationInSeconds / 60);
          const hours = Math.floor(durationInMinutes / 60);
          const minutes = durationInMinutes % 60;
          
          const timeString = hours > 0 
            ? `${hours}h ${minutes}min`
            : `${minutes} min`;
          
          // Calculate distance in a readable format
          const distanceInMeters = leg.distance;
          const distanceInKm = (distanceInMeters / 1000).toFixed(1);
          
          routeResults[endLocation.id] = {
            time: timeString,
            distance: `${distanceInKm} km`,
            durationSeconds: durationInSeconds,
            distanceMeters: distanceInMeters
          };
        }
      } catch (error) {
        console.error(`Error calculating route segment ${i-1} to ${i}:`, error);
        
        // Fall back to straight-line calculation for this segment
        const dx = (endLocation.longitude - startLocation.longitude) * 111.32 * Math.cos(endLocation.latitude * (Math.PI / 180));
        const dy = (endLocation.latitude - startLocation.latitude) * 110.574;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Estimate time (assuming 60 km/h average speed)
        const timeInHours = distance / 60;
        const timeInMinutes = Math.round(timeInHours * 60);
        const distanceInMeters = distance * 1000;
        
        routeResults[endLocation.id] = {
          distance: `${distance.toFixed(1)} km`,
          time: timeInMinutes < 60 
            ? `${timeInMinutes} min` 
            : `${Math.floor(timeInMinutes / 60)}h ${timeInMinutes % 60}min`,
          durationSeconds: timeInMinutes * 60,
          distanceMeters: distanceInMeters
        };
        
        // Add a straight line for this segment
        allCoordinates.push([endLocation.longitude, endLocation.latitude]);
      }
    }

    // Create a combined geometry from all the segments
    const combinedGeometry = {
      type: 'LineString',
      coordinates: allCoordinates
    };

    // Return both the route data for each location and the combined route geometry
    return {
      routes: routeResults,
      routeGeometry: combinedGeometry
    };
  } catch (error) {
    console.error('Error calculating routes:', error);
    // Fall back to straight-line distance calculation for all segments
    return calculateStraightLineDistances(sortedLocations);
  }
};

/**
 * Calculate straight-line distances as a fallback method
 */
export const calculateStraightLineDistances = (
  locations: SavedLocation[]
): { 
  routes: {[locationId: string]: LocationRouteInfo}, 
  routeGeometry: any 
} => {
  const routeResults: {[locationId: string]: LocationRouteInfo} = {};
  const coordinates: [number, number][] = [];
  
  // Add all location coordinates to the geometry
  locations.forEach(loc => {
    coordinates.push([loc.longitude, loc.latitude]);
  });

  // Calculate routes between consecutive locations
  locations.forEach((location, index) => {
    if (index > 0) {
      const prevLocation = locations[index - 1];
      
      // Calculate distance in km (simple Euclidean distance)
      const dx = (location.longitude - prevLocation.longitude) * 111.32 * Math.cos(location.latitude * (Math.PI / 180));
      const dy = (location.latitude - prevLocation.latitude) * 110.574;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Convert km to meters
      const distanceInMeters = distance * 1000;
      
      // Estimate time (assuming 60 km/h average speed)
      const timeInHours = distance / 60;
      const timeInMinutes = Math.round(timeInHours * 60);
      const durationInSeconds = timeInMinutes * 60;
      
      routeResults[location.id] = {
        distance: `${distance.toFixed(1)} km`,
        time: timeInMinutes < 60 
          ? `${timeInMinutes} min` 
          : `${Math.floor(timeInMinutes / 60)}h ${timeInMinutes % 60}min`,
        durationSeconds: durationInSeconds,
        distanceMeters: distanceInMeters
      };
    }
  });

  // Create a simple straight-line GeoJSON geometry
  const routeGeometry = {
    type: 'LineString',
    coordinates: coordinates
  };

  return {
    routes: routeResults,
    routeGeometry: routeGeometry
  };
};

/**
 * Calculate routes from a provided current location to a destination
 */
export const calculateRouteFromCurrentLocation = async (
  currentLocation: {latitude: number, longitude: number},
  destinationLocation: {latitude: number, longitude: number}
): Promise<{routeInfo: LocationRouteInfo | null, routeGeometry: any}> => {
  if (!currentLocation || !destinationLocation) {
    return {routeInfo: null, routeGeometry: null};
  }

  try {
    // Build coordinates string for the API request "current_lng,current_lat;destination_lng,destination_lat"
    const coordinates = `${currentLocation.longitude},${currentLocation.latitude};${destinationLocation.longitude},${destinationLocation.latitude}`;

    try {
      // Make the request to the Mapbox Directions API
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}`;
      const response = await axios.get(url, {
        params: {
          access_token: MAPBOX_TOKEN,
          geometries: 'geojson',
          overview: 'full',
          steps: 'false',
          alternatives: false,
          exclude: 'ferry',
          annotations: 'distance,duration'
        }
      });

      // Process the response
      const directions = response.data;
      
      if (!directions.routes || directions.routes.length === 0) {
        throw new Error('No route found');
      }

      // Get the main route
      const route = directions.routes[0];
      
      // Get the first leg (there should be only one in this case)
      if (route.legs && route.legs.length > 0) {
        const leg = route.legs[0];
        
        // Calculate time in a readable format
        const durationInSeconds = leg.duration;
        const durationInMinutes = Math.round(durationInSeconds / 60);
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        
        const timeString = hours > 0 
          ? `${hours}h ${minutes}min`
          : `${minutes} min`;
        
        // Calculate distance in a readable format
        const distanceInMeters = leg.distance;
        const distanceInKm = (distanceInMeters / 1000).toFixed(1);
        
        return {
          routeInfo: {
            time: timeString,
            distance: `${distanceInKm} km`,
            durationSeconds: durationInSeconds,
            distanceMeters: distanceInMeters
          },
          routeGeometry: route.geometry
        };
      }
    } catch (error) {
      console.error('Error calculating route from current location:', error);
      // Fall back to straight-line distance calculation
      const routeInfo = calculateStraightLineDistance(
        currentLocation.latitude, 
        currentLocation.longitude, 
        destinationLocation.latitude, 
        destinationLocation.longitude
      );
      
      // Create a simple straight-line GeoJSON geometry
      const routeGeometry = {
        type: 'LineString',
        coordinates: [
          [currentLocation.longitude, currentLocation.latitude],
          [destinationLocation.longitude, destinationLocation.latitude]
        ]
      };
      
      return {routeInfo, routeGeometry};
    }
  } catch (error) {
    console.error('Error calculating route:', error);
    return {routeInfo: null, routeGeometry: null};
  }
  
  return {routeInfo: null, routeGeometry: null};
};

/**
 * Get current browser position
 */
const getCurrentPosition = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    });
  });
};

/**
 * Calculate straight-line distance between two points
 */
const calculateStraightLineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): LocationRouteInfo => {
  // Calculate distance in km (simple Euclidean distance)
  const dx = (lng2 - lng1) * 111.32 * Math.cos(lat2 * (Math.PI / 180));
  const dy = (lat2 - lat1) * 110.574;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Convert km to meters
  const distanceInMeters = distance * 1000;
  
  // Estimate time (assuming 60 km/h average speed)
  const timeInHours = distance / 60;
  const timeInMinutes = Math.round(timeInHours * 60);
  const durationInSeconds = timeInMinutes * 60;
  
  return {
    distance: `${distance.toFixed(1)} km`,
    time: timeInMinutes < 60 
      ? `${timeInMinutes} min` 
      : `${Math.floor(timeInMinutes / 60)}h ${timeInMinutes % 60}min`,
    durationSeconds: durationInSeconds,
    distanceMeters: distanceInMeters
  };
}; 